"use client"

import { FC, useState } from "react"
import { Content } from "@prismicio/client"
import { PrismicRichText, SliceComponentProps } from "@prismicio/react"
import { PrismicNextImage } from "@prismicio/next"
import {
  LazyMotion,
  domAnimation,
  m,
  motion,
  type Variants,
} from "motion/react"
import { vFadeUp, vStaggerContainer } from "@/lib/animations"
import { cn, sliceLabel } from "@/lib/utils"

export type HeadingHighlightedInfoBlockProps =
  SliceComponentProps<Content.HeadingHighlightedInfoBlockSlice>

const iconBgMap: Record<string, string> = {
  Orange: "bg-gradient-fire",
  Pink: "bg-gradient-purple",
  Blue: "bg-gradient-blue",
  Green: "bg-gradient-green",
  Brown: "bg-gradient-amber",
  Yellow: "bg-gradient-amber",
}

// Accent color per icon bg for section markers
const accentMap: Record<string, string> = {
  Orange: "var(--sacred-orange)",
  Pink: "oklch(0.6 0.2 330)",
  Blue: "oklch(0.55 0.2 250)",
  Green: "oklch(0.55 0.18 150)",
  Brown: "oklch(0.59 0.17 42)",
  Yellow: "oklch(0.59 0.17 42)",
}

const vSectionRow: Variants = {
  hidden: { opacity: 0, x: -16 },
  visible: {
    opacity: 1,
    x: 0,
    transition: { duration: 0.45, ease: "easeOut" },
  },
}

const HeadingHighlightedInfoBlock: FC<HeadingHighlightedInfoBlockProps> = ({
  slice,
}) => {
  const { primary } = slice
  const [activeIdx, setActiveIdx] = useState<number | null>(null)

  const colorKey = primary.icon_background_color ?? "Orange"
  const iconBg = iconBgMap[colorKey] ?? "bg-gradient-fire"
  const accent = accentMap[colorKey] ?? "var(--sacred-orange)"

  return (
    <LazyMotion features={domAnimation}>
      <section
        data-slice-type={slice.slice_type}
        data-slice-variation={slice.variation}
        aria-label={sliceLabel(slice.slice_type)}
        className="relative overflow-hidden section-py"
        style={{
          background:
            "radial-gradient(ellipse 100% 80% at 50% 0%, oklch(0.97 0.04 75) 0%, white 70%)",
        }}
      >
        {/* Ambient glow keyed to accent color */}
        <div
          aria-hidden
          className="pointer-events-none absolute top-0 left-0 size-72 -translate-1/3 rounded-full opacity-20 blur-3xl"
          style={{ background: accent }}
        />
        <div
          aria-hidden
          className="pointer-events-none absolute right-0 bottom-0 size-72 translate-1/3 rounded-full opacity-10 blur-3xl"
          style={{ background: accent }}
        />

        <m.div
          className="relative container flex items-center justify-center"
          variants={vStaggerContainer}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-80px" }}
        >
          <div className="flex w-full max-w-3xl flex-col gap-8">
            {/* Icon + Title */}
            <m.div variants={vFadeUp} className="flex items-center gap-5">
              {primary.icon?.url && (
                <m.div
                  className={cn(
                    "relative flex size-16 shrink-0 items-center justify-center rounded-2xl shadow-lg",
                    iconBg,
                  )}
                  whileHover={{ scale: 1.08, rotate: 4 }}
                  transition={{ type: "spring", stiffness: 300, damping: 15 }}
                >
                  {/* Icon halo */}
                  <div
                    className="absolute inset-0 rounded-2xl opacity-60 blur-md"
                    style={{ background: accent }}
                  />
                  <PrismicNextImage
                    field={primary.icon}
                    fallbackAlt=""
                    width={36}
                    height={36}
                    className="relative size-9 object-contain brightness-200 drop-shadow-sm"
                    imgixParams={{ auto: ["format", "compress"], q: 75 }}
                  />
                </m.div>
              )}
              {primary.title && (
                <div className="text-gradient-red">
                  <PrismicRichText field={primary.title} />
                </div>
              )}
            </m.div>

            {/* Accent line */}
            <m.div
              variants={{
                hidden: { scaleX: 0, opacity: 0 },
                visible: {
                  scaleX: 1,
                  opacity: 1,
                  transition: { duration: 0.8, ease: "easeOut" },
                },
              }}
              className="h-px origin-left rounded-full"
              style={{
                background: `linear-gradient(to right, ${accent}, transparent)`,
              }}
            />

            {/* Info sections */}
            {primary.info_sections && primary.info_sections.length > 0 && (
              <m.div
                variants={vStaggerContainer}
                className="relative overflow-hidden rounded-3xl border bg-white/80 shadow-sm backdrop-blur-sm"
                style={{
                  borderColor: `color-mix(in oklch, ${accent} 25%, transparent)`,
                }}
              >
                {/* Left accent strip */}
                <div
                  className="absolute inset-y-0 left-0 w-1 rounded-l-3xl"
                  style={{
                    background: `linear-gradient(to bottom, ${accent}, transparent)`,
                  }}
                />

                <div className="flex flex-col">
                  {primary.info_sections.map((section, i) => (
                    <m.div
                      key={i}
                      variants={vSectionRow}
                      className={cn(
                        "relative flex cursor-default flex-col gap-2 px-7 py-5 transition-colors duration-200",
                        i !== 0 && "border-t",
                        activeIdx === i
                          ? "bg-orange-50/60"
                          : "hover:bg-orange-50/30",
                      )}
                      style={{
                        borderColor: `color-mix(in oklch, ${accent} 15%, transparent)`,
                      }}
                      onMouseEnter={() => setActiveIdx(i)}
                      onMouseLeave={() => setActiveIdx(null)}
                    >
                      {/* Hover left bar */}
                      <motion.div
                        className="absolute inset-y-3 left-0 w-0.5 rounded-full"
                        style={{ background: accent }}
                        initial={{ scaleY: 0, opacity: 0 }}
                        animate={
                          activeIdx === i
                            ? { scaleY: 1, opacity: 1 }
                            : { scaleY: 0, opacity: 0 }
                        }
                        transition={{ duration: 0.2 }}
                      />

                      {section.title && (
                        <h4
                          className="text-sm font-extrabold tracking-wide uppercase"
                          style={{ color: accent }}
                        >
                          {section.title}
                        </h4>
                      )}
                      {section.description && (
                        <PrismicRichText
                          field={section.description}
                          components={{
                            listItem: ({ children }) => (
                              <li className="text-sm/relaxed text-muted-foreground">
                                {children}
                              </li>
                            ),
                            oListItem: ({ children }) => (
                              <li className="text-sm/relaxed text-muted-foreground">
                                {children}
                              </li>
                            ),
                            paragraph: ({ children }) => (
                              <p className="text-sm/relaxed text-muted-foreground">
                                {children}
                              </p>
                            ),
                            strong: ({ children }) => (
                              <strong className="font-bold text-foreground">
                                {children}
                              </strong>
                            ),
                          }}
                        />
                      )}
                    </m.div>
                  ))}
                </div>
              </m.div>
            )}
          </div>
        </m.div>
      </section>
    </LazyMotion>
  )
}

export default HeadingHighlightedInfoBlock
